import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getUsers, getTasks } from '../data/supabaseApi';
import { useAuth } from '../context/AuthContext';
import {
  FiUsers,
  FiUser,
  FiLogOut,
  FiChevronDown,
  FiChevronUp,
  FiSearch,
  FiX
} from 'react-icons/fi';
import './Sidebar.css';

const MemberItem = ({ member, selectedMember, onMemberSelect, getMemberInitials }) => {
  const [stats, setStats] = useState({ completed: 0, active: 0 });

  useEffect(() => {
    let cancelled = false;
    getTasks().then((allTasks) => {
      if (cancelled) return;
      const memberTasks = allTasks.filter(t => (t.assignedMember || t.userName) === member.name);
      const status = (t) => t.currentStatus || t.status;
      const completed = memberTasks.filter(t => status(t) === 'Completed').length;
      const active = memberTasks.filter(t => !['Completed', 'Blocked', 'Delayed'].includes(status(t))).length;
      setStats({ completed, active });
    });
    return () => { cancelled = true; };
  }, [member.name]);

  return (
    <button
      className={`nav-item member-item ${selectedMember === member.name ? 'active' : ''}`}
      onClick={() => {
        if (onMemberSelect) onMemberSelect(member.name);
      }}
    >
      <div className="member-avatar">
        {getMemberInitials(member.name)}
      </div>
      <div className="member-info">
        <div className="member-name">{member.name}</div>
        <div className="member-stats">
          <span className="stat-item">
            <span className="stat-value">{stats.completed}</span>
            <span className="stat-label">completed</span>
          </span>
          {stats.active > 0 && (
            <span className="stat-item">
              <span className="stat-value active">{stats.active}</span>
              <span className="stat-label">active</span>
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

const Sidebar = ({ userRole, selectedMember, onMemberSelect }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();
  const [members, setMembers] = useState([]);
  const [isMembersExpanded, setIsMembersExpanded] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (userRole === 'admin') {
      getUsers().then((list) => {
        setMembers(list.filter(u => u.role === 'member'));
      });
    }
  }, [userRole]);

  const handleLogout = async () => {
    await signOut();
    navigate('/', { replace: true });
  };

  const getMemberInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const filteredMembers = members.filter(member =>
    member.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <h2>Task Tracker</h2>
      </div>
      <nav className="sidebar-nav">
        {userRole === 'admin' ? (
          <>
            <button
              className={`nav-item ${!selectedMember && location.pathname === '/admin' ? 'active' : ''}`}
              onClick={() => {
                if (onMemberSelect) onMemberSelect(null);
                navigate('/admin');
              }}
            >
              <FiUsers className="nav-icon" />
              <span>All Members</span>
            </button>
            <div className="sidebar-divider"></div>

            <div className="sidebar-section-header">
              <div className="sidebar-section-title">Team Members</div>
              <button
                className="collapse-btn"
                onClick={() => setIsMembersExpanded(!isMembersExpanded)}
              >
                {isMembersExpanded ? <FiChevronUp /> : <FiChevronDown />}
              </button>
            </div>

            {isMembersExpanded && (
              <>
                <div className="sidebar-search">
                  <FiSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search members..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="member-search-input"
                  />
                  {searchQuery && (
                    <button
                      className="clear-search-btn"
                      onClick={() => setSearchQuery('')}
                    >
                      <FiX />
                    </button>
                  )}
                </div>

                {filteredMembers.length === 0 ? (
                  <div className="no-members-found">No members found</div>
                ) : (
                  filteredMembers.map(member => (
                    <MemberItem
                      key={member.id}
                      member={member}
                      selectedMember={selectedMember}
                      onMemberSelect={onMemberSelect}
                      getMemberInitials={getMemberInitials}
                    />
                  ))
                )}
              </>
            )}
          </>
        ) : (
          <button
            className={`nav-item ${location.pathname === '/member' ? 'active' : ''}`}
            onClick={() => navigate('/member')}
          >
            <FiUser className="nav-icon" />
            <span>My Dashboard</span>
          </button>
        )}
      </nav>
      <div className="sidebar-footer">
        <button type="button" className="logout-btn" onClick={handleLogout}>
          <FiLogOut className="logout-icon" />
          <span>Sign out</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
